import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Sidebar from "./Sidebar";
import ThemeToggle from "../common/ThemeToggle";
import Breadcrumbs from "../common/Breadcrumbs";
import ConfirmationDialog from "../common/ConfirmationDialog";
import BrandLogo from "../common/BrandLogo";
import { useAuth } from "../../hooks/useAuth";

const AppShell = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsLogoutModalOpen(false);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-surface-light transition-colors duration-300 dark:bg-slate-950">
      <Sidebar
        isMobileOpen={isSidebarOpen}
        onMobileClose={() => setIsSidebarOpen(false)}
        isCollapsed={isDesktopSidebarCollapsed}
      />
      
      <div className="flex min-h-0 flex-1 flex-col min-w-0 overflow-hidden">
        <header className="flex h-12 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/80 sm:px-6 lg:px-8 z-20">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setIsSidebarOpen(true)}
              className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 lg:hidden"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="hidden lg:block">
              <Breadcrumbs />
            </div>
            <Link to="/dashboard" className="lg:hidden">
              <BrandLogo size="sm" />
            </Link>
          </div>
          
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="h-6 w-[1px] bg-slate-200 dark:bg-slate-800 mx-1 hidden sm:block"></div>
            
            {/* User Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2.5 rounded-xl p-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-500 text-[10px] font-bold text-white shadow-lg shadow-primary-500/20">
                  {user?.name?.charAt(0) || user?.fullName?.charAt(0) || "U"}
                </div>
                <div className="hidden sm:flex items-center gap-1.5 mr-1 text-left">
                  <span className="text-[11px] font-bold text-slate-900 dark:text-white">
                    {user?.name || user?.fullName}
                  </span>
                  {user?.role && (
                    <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                      ({user.role})
                    </span>
                  )}
                </div>
                <svg
                  className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 origin-top-right rounded-xl border border-slate-200 bg-white p-1 shadow-xl dark:border-slate-800 dark:bg-slate-900 z-50">
                  <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 mb-1">
                    <p className="truncate text-xs font-bold text-slate-900 dark:text-white">
                      {user?.name || user?.fullName}
                      {user?.role && (
                        <span className="ml-1 text-[10px] font-medium text-slate-500 dark:text-slate-400">
                          ({user.role})
                        </span>
                      )}
                    </p>
                  </div>
                  
                  {/* <Link
                    to="/profile"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-primary-600 dark:text-slate-400 dark:hover:bg-slate-800/50"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </Link> */}
                  
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setIsLogoutModalOpen(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-danger transition-colors hover:bg-danger/5"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto bg-surface-light p-2 transition-colors duration-300 dark:bg-slate-950 sm:p-2 lg:p-2">
          <div className="mx-auto max-w-full lg:px-2">
            <div className="lg:hidden mb-4">
              <Breadcrumbs />
            </div>
            {children}
          </div>
        </main>
      </div>

      <ConfirmationDialog
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
        title="Sign Out"
        message="Are you sure you want to sign out of your account?"
        confirmText="Sign Out"
        variant="danger"
      />
    </div>
  );
};

export default AppShell;
